/**
 * 学习乐园 · 山脉河流归位（game-shanmai-heliu.js）
 * ============================================================
 * 「版图归位」第二作：复用省份拼图同一张底图（全局 STSF_MAP），只换题目层。
 * 题材三组（数据在 STSF_MAP.features，scripts/study-shanmai-features.json
 * 手编示意走向，与省界同一套 Albers 投影，严格对齐底图）：
 *   大江大河（11）/ 主要山脉（12）/ 五岳名湖（10）。
 *
 * 交互（点选—点放两步，与省份拼图同款，不依赖拖拽）：
 *   托盘点要素名 → 选中 → 点地图上它对应的示意线 / 点。
 *   命中判定是「点到折线 / 点的最近距离」：地图上点一下，取最近的要素，
 *   距离在容差内才算点中——容差随渲染宽度动态放缩（手机上同一指头
 *   对应更多 viewBox 单位），桌面触屏都不吃亏。
 *
 * 反馈底线（与其他游戏同一产品哲学）：
 *   放对了 → 数据里手写的一句讲解（这条河多长、在哪入海，这座山分隔着什么）；
 *   放错了 → 告诉你落点是谁：点到别的要素，讲那个要素是谁、目标在它哪个方向；
 *            点在陆地上没挨着任何要素，讲落点在哪省 + 目标在哪个方向
 *            （省份判定复用省界环点数组的射线法——底图数据的第二份用途）；
 *            点到海面或国境之外，也如实说。
 *   方位由两要素锚点（ax/ay）在主图坐标系实算，不是事先编好的台词。
 *
 * 示意口径（页面文案同步说明，见 doc/shanmai-heliu.html）：
 *   要素是教育用示意走向，非精确河道 / 山脊线；底图合规与省份拼图同源
 *   ——台湾省、香港与澳门（放大框）、南海诸岛十段线（小图框）一个不少。
 *
 * 简化红线（同省份拼图）：不做中断续局，刷新即重开；成绩只存本机
 * localStorage（按题材分档），不上传。
 *
 * 冒烟钩子：window.STShanmai 暴露纯引擎函数（distToPolyline / nearestFeature /
 * bearingWord / missExplain / shuffledIds / describeZone），供确定性用例。
 */
(function (global) {
  'use strict';

  var MAP = global.STSF_MAP;
  var RECORD_KEY = 'study-shanmai-record';
  var HINT_MS = 2200;
  var TOL_BASE = 28;    // 命中容差下限（viewBox 单位）——2026-10-01 用户反馈难点中，22→28
  var TOL_SCALE = 24;   // 动态放缩：TOL = max(BASE, SCALE * 1000 / 渲染宽)；手机窄屏下实际命中半径 ~24px

  var TOPICS = [
    { id: 'river', label: '大江大河' },
    { id: 'mountain', label: '主要山脉' },
    { id: 'peak', label: '五岳名湖' },
  ];

  /* ============ 纯引擎（无 DOM） ============ */

  /** 点到折线的最短距离（pts = [[x,y], …]；单点即到点距离） */
  function distToPolyline(x, y, pts) {
    if (pts.length === 1) return Math.hypot(x - pts[0][0], y - pts[0][1]);
    var best = Infinity;
    for (var i = 0; i < pts.length - 1; i++) {
      var ax = pts[i][0], ay = pts[i][1], bx = pts[i + 1][0], by = pts[i + 1][1];
      var dx = bx - ax, dy = by - ay;
      var len2 = dx * dx + dy * dy;
      var t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len2));
      var d = Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
      if (d < best) best = d;
    }
    return best;
  }

  /** 射线法：点是否在环集合内（省份落点讲解用，直接吃 STSF_MAP 的环点数组） */
  function pointInRings(x, y, rings) {
    for (var k = 0; k < rings.length; k++) {
      var ring = rings[k];
      var inside = false;
      for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        var xi = ring[i][0], yi = ring[i][1];
        var xj = ring[j][0], yj = ring[j][1];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      if (inside) return true;
    }
    return false;
  }

  function inBox(b, x, y) {
    return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  }

  /**
   * 落点省份：主图环 → 港澳放大框环（放大框坐标与主图坐标不同系，
   * 先判框内再查 zoomPoints——口径与省份拼图的命中顺序一致）。
   * 返回 region 或 null（海 / 国境之外）。
   */
  function provinceAt(x, y) {
    if (inBox(MAP.zoomBox, x, y)) {
      for (var i = 0; i < MAP.regions.length; i++) {
        var zr = MAP.regions[i];
        if (zr.zoomPoints.length && pointInRings(x, y, zr.zoomPoints)) return zr;
      }
      return null;
    }
    for (var j = 0; j < MAP.regions.length; j++) {
      var r = MAP.regions[j];
      if (r.points.length && pointInRings(x, y, r.points)) return r;
    }
    return null;
  }

  /**
   * 最近的要素（不管归位与否——放错讲解需要「落点是谁」，包括已归位的）。
   * 返回 { f, d, hit }：hit = d 是否在动态容差内。
   */
  function nearestFeature(x, y, feats, tol) {
    var best = null, bestD = Infinity;
    for (var i = 0; i < feats.length; i++) {
      var f = feats[i];
      var d = distToPolyline(x, y, f.pts || [f.pt]);
      if (d < bestD) { bestD = d; best = f; }
    }
    return { f: best, d: bestD, hit: !!best && bestD <= tol };
  }

  /** 8 方位词：a 在 b 的哪个方向（统一主图坐标系 ax/ay；y 向下 = 向南） */
  function bearingWord(a, b) {
    var dx = a.ax - b.ax, dy = a.ay - b.ay;
    if (Math.hypot(dx, dy) < 34) return '就在旁边';
    var deg = Math.atan2(dy, dx) / Math.PI * 180; // 0° 正东、90° 正南
    var words = ['正东', '东南', '正南', '西南', '正西', '西北', '正北', '东北'];
    return words[Math.round(((deg + 360) % 360) / 45) % 8];
  }

  /** 错放讲解：落点是别的要素——那里是谁 + 想放的要素在它哪个方向 */
  function missExplain(sel, target) {
    var where = bearingWord(sel, target);
    if (where === '就在旁边') {
      return '你点的是' + target.short + '——' + sel.short + '跟它挨得很近，再仔细看看。';
    }
    return '你点的是' + target.short + '——' + sel.short + '在它的' + where + '。';
  }

  function shuffledIds(n) {
    var ids = [];
    for (var i = 0; i < n; i++) ids.push(i);
    for (var j = n - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var t = ids[j]; ids[j] = ids[k]; ids[k] = t;
    }
    return ids;
  }

  /** 提示用的模糊分区说明（够定位、不剧透；阈值与省份拼图同款） */
  function describeZone(f) {
    var z = f.ay < 260 ? '北方' : (f.ay > 500 ? '南方' : '中部');
    if (f.ax < 330) z += '偏西';
    else if (f.ax > 600) z += '偏东';
    return z;
  }

  /* ============ 渲染与交互 ============ */

  var NS = 'http://www.w3.org/2000/svg';
  var state = {
    topic: 'river', selected: null, placed: {}, placedCount: 0,
    miss: 0, hints: 0, elapsed: 0, timer: null, done: false,
  };
  var el = {};
  var svg, feats, labels, nodes;

  function svgEl(tag, attrs) {
    var n = document.createElementNS(NS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }

  function fmtTime(ms) {
    var s = Math.floor(ms / 1000);
    return Math.floor(s / 60) + ' 分 ' + (s % 60) + ' 秒';
  }

  function topicMeta() {
    for (var i = 0; i < TOPICS.length; i++) {
      if (TOPICS[i].id === state.topic) return TOPICS[i];
    }
    return TOPICS[0];
  }

  function say(msg, tone) {
    // 语义 → 既有状态行修饰类（同省份拼图）：bad=警示、done=结算
    var TONE_CLS = { ok: '', bad: 'is-warn', done: 'is-over' };
    el.status.textContent = msg;
    el.status.className = 'st-status' + (tone && TONE_CLS[tone] ? ' ' + TONE_CLS[tone] : '');
  }

  function updateHud() {
    el.progress.textContent = '已归位 ' + state.placedCount + ' / ' + feats.length;
    el.time.textContent = '用时 ' + fmtTime(state.elapsed);
  }

  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function renderRecord() {
    var rec = (loadRecord())[state.topic];
    if (!rec || !rec.bestMs) {
      el.record.innerHTML = '<p class="st-map-record-empty">这个题材还没有完整归位的记录——放完第一局就有了。</p>';
      return;
    }
    el.record.innerHTML =
      '<p>「' + topicMeta().label + '」最快完整归位：<strong>' + fmtTime(rec.bestMs) + '</strong>（错放 ' + rec.bestMiss + ' 次）</p>' +
      '<p class="st-map-record-sub">完整归位 ' + (rec.plays || 1) + ' 局 · 成绩只存这台设备</p>';
  }

  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 存不进就算了 */ }
  }

  /* ---- SVG 搭建：底图（省界浅底）→ 港澳框 / 南海框 → 要素层 → 标注层 ---- */

  function buildSvg() {
    svg = svgEl('svg', {
      viewBox: MAP.viewBox,
      class: 'st-map-svg',
      role: 'application',
      'aria-label': '中国版图：把托盘里的江河山脉放回对应位置',
    });
    nodes = {};
    labels = {};

    // 1) 省界浅底（本页省份不参与拼，只是「陆地」的底色；港澳主图无块天然跳过）
    MAP.regions.forEach(function (r) {
      if (!r.main) return;
      svg.appendChild(svgEl('path', { d: r.main, class: 'st-map-slot' }));
    });

    // 2) 港澳放大框 + 南海小图框：合规要素与省份拼图同一套，一个不少
    var zb = MAP.zoomBox;
    svg.appendChild(svgEl('polyline', {
      points: zb.anchor[0] + ',' + zb.anchor[1] + ' ' + (zb.x + zb.w) + ',' + (zb.y + 26) + ' ' + (zb.x + zb.w - 10) + ',' + (zb.y + 26),
      class: 'st-map-leader',
    }));
    svg.appendChild(svgEl('circle', { cx: zb.anchor[0], cy: zb.anchor[1], r: 3.5, class: 'st-map-anchor' }));
    svg.appendChild(svgEl('rect', { x: zb.x, y: zb.y, width: zb.w, height: zb.h, rx: 3, class: 'st-map-zoombox' }));
    MAP.regions.forEach(function (r) {
      if (!r.zoom) return;
      svg.appendChild(svgEl('path', { d: r.zoom, class: 'st-map-slot' }));
    });
    var zl = svgEl('text', { x: zb.x + 12, y: zb.y + 22, class: 'st-map-note' });
    zl.textContent = zb.label;
    svg.appendChild(zl);

    var sb = MAP.sea.box;
    svg.appendChild(svgEl('rect', { x: sb.x, y: sb.y, width: sb.w, height: sb.h, rx: 3, class: 'st-map-seabox' }));
    svg.appendChild(svgEl('path', { d: MAP.sea.lines, class: 'st-map-segline' }));
    MAP.sea.islands.forEach(function (isl) {
      var rad = Math.max(1, Math.min(3.2, Math.sqrt(isl[2]) / 9));
      svg.appendChild(svgEl('circle', { cx: isl[0], cy: isl[1], r: rad, class: 'st-map-island' }));
    });
    var sl = svgEl('text', { x: sb.x + sb.w / 2, y: sb.y + 20, class: 'st-map-note st-map-note--center' });
    sl.textContent = MAP.sea.label;
    svg.appendChild(sl);

    // 3) 要素层：线（河 / 山）为虚线灰、点（岳 / 湖）为灰点——归位后亮品牌色
    feats.forEach(function (f) {
      var n;
      if (f.pt) {
        n = svgEl('circle', { cx: f.pt[0], cy: f.pt[1], r: 6, class: 'st-map-peak' });
      } else {
        n = svgEl('path', { d: ptsToPath(f.pts), class: 'st-map-feat' });
      }
      n.dataset.id = f.id;
      nodes[f.id] = n;
      svg.appendChild(n);
    });

    // 4) 标注层：归位后逐个亮出名字（岳 / 湖标注在点上方，线标注在中位锚点）
    feats.forEach(function (f) {
      var x = f.pt ? f.pt[0] : f.ax;
      var y = f.pt ? f.pt[1] - 9 : f.ay + 4;
      var t = svgEl('text', { x: x, y: y, class: 'st-map-label', 'text-anchor': 'middle', visibility: 'hidden' });
      t.textContent = f.short;
      labels[f.id] = t;
      svg.appendChild(t);
    });

    el.map.innerHTML = '';
    el.map.appendChild(svg);
    svg.addEventListener('click', onPick);
  }

  function ptsToPath(pts) {
    var d = '';
    for (var i = 0; i < pts.length; i++) {
      d += (i === 0 ? 'M' : 'L') + pts[i][0] + ' ' + pts[i][1];
    }
    return d;
  }

  function selectFeature(f) {
    state.selected = f;
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('is-active', btns[i].dataset.stPiece === f.id);
    }
    say('已选中「' + f.name + '」——点地图上它对应的线条或点；放错了我会告诉你那里是谁。');
  }

  function clearSelection() {
    state.selected = null;
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) btns[i].classList.remove('is-active');
  }

  function placeCorrect(f) {
    state.placed[f.id] = true;
    state.placedCount++;
    nodes[f.id].classList.add('is-placed');
    var btn = el.tray.querySelector('[data-st-piece="' + f.id + '"]');
    if (btn) {
      btn.classList.add('is-done');
      btn.disabled = true;
      btn.removeAttribute('data-st-piece');
    }
    labels[f.id].setAttribute('visibility', 'visible');
    clearSelection();
    updateHud();
    if (state.placedCount === feats.length) {
      finish();
    } else {
      say('对！' + f.tip + '（' + state.placedCount + '/' + feats.length + '）', 'ok');
    }
  }

  function finish() {
    state.done = true;
    stopTimer();
    var all = loadRecord();
    var rec = all[state.topic] || {};
    var first = !rec.bestMs;
    var isNew = first || state.elapsed < rec.bestMs;
    var prevBest = rec.bestMs;
    rec.bestMs = first ? state.elapsed : Math.min(rec.bestMs, state.elapsed);
    if (isNew) rec.bestMiss = state.miss;
    rec.plays = (rec.plays || 0) + 1;
    all[state.topic] = rec;
    saveRecord(all);
    renderRecord();
    var extra = first
      ? '这是「' + topicMeta().label + '」的第一个完整归位记录！'
      : (isNew ? '新纪录！此前最快是 ' + fmtTime(prevBest) + '。' : '此前最快是 ' + fmtTime(rec.bestMs) + '。');
    say('「' + topicMeta().label + '」全部归位！用时 ' + fmtTime(state.elapsed) + '，错放 ' + state.miss +
      ' 次，提示 ' + state.hints + ' 次。' + extra + '换个题材再战一局。', 'done');
  }

  /* ---- 落点判定 ---- */

  function tolPx() {
    var rect = svg.getBoundingClientRect();
    var w = rect.width > 0 ? rect.width : 1000;
    return Math.max(TOL_BASE, TOL_SCALE * 1000 / w);
  }

  function onPick(ev) {
    if (state.done) return;
    var p = toViewBox(ev);
    var near = nearestFeature(p[0], p[1], feats, tolPx());

    if (!state.selected) {
      // 未选中：不剧透——只说「先去托盘挑名字」，顺带报一下落点省份
      var prov = provinceAt(p[0], p[1]);
      if (inBox(MAP.sea.box, p[0], p[1])) {
        say('右下角是南海诸岛小图框（海南省三沙市管辖），不参与归位。');
      } else if (inBox(MAP.zoomBox, p[0], p[1])) {
        say('这个框是香港和澳门的放大示意，不参与归位——先在托盘里挑一个名字。');
      } else {
        say('这里是' + (prov ? prov.name : '海面或国境之外') + '——先在托盘里挑一个名字，再点地图放它。');
      }
      return;
    }

    var sel = state.selected;
    if (near.hit && near.f.id === sel.id) {
      placeCorrect(sel);
      return;
    }

    // —— 未命中：分口径讲清为什么 ——
    state.miss++;
    if (near.hit && state.placed[near.f.id]) {
      say(near.f.short + '已经归位了——' + sel.short + '在它的' + bearingWord(sel, near.f) + '。', 'bad');
    } else if (near.hit) {
      say(missExplain(sel, near.f), 'bad');
    } else if (inBox(MAP.sea.box, p[0], p[1])) {
      say('那是南海诸岛小图框，不参与归位——' + sel.short + '在' + describeZone(sel) + '。', 'bad');
    } else if (inBox(MAP.zoomBox, p[0], p[1])) {
      say('这个框是香港、澳门的放大示意——' + sel.short + '不在这里。', 'bad');
    } else {
      var prov2 = provinceAt(p[0], p[1]);
      if (prov2) {
        say('这里是' + prov2.name + '——' + sel.short + '在它的' + bearingWord(sel, prov2) + '。', 'bad');
      } else {
        say('这里是海面或国境之外——' + sel.short + '在' + describeZone(sel) + '。再想想它该在哪。', 'bad');
      }
    }
    clearSelection();
  }

  function toViewBox(ev) {
    var rect = svg.getBoundingClientRect();
    return [
      (ev.clientX - rect.left) / rect.width * 1000,
      (ev.clientY - rect.top) / rect.height * 800,
    ];
  }

  /* ---- 托盘 / 题材 / 计时 / 按钮 ---- */

  function buildTray() {
    el.tray.innerHTML = '';
    shuffledIds(feats.length).forEach(function (idx) {
      var f = feats[idx];
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'st-map-piece';
      b.dataset.stPiece = f.id;
      b.textContent = f.name;
      b.addEventListener('click', function () {
        if (state.done) return;
        if (state.selected && state.selected.id === f.id) {
          clearSelection();
          say('已取消选中。');
          return;
        }
        selectFeature(f);
      });
      el.tray.appendChild(b);
    });
  }

  function markTopicBtns() {
    var btns = el.topics.querySelectorAll('[data-st-topic]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('is-active', btns[i].dataset.stTopic === state.topic);
    }
  }

  function stopTimer() {
    if (state.timer) { clearInterval(state.timer); state.timer = null; }
  }

  /** 开新一局（换题材也走这里）：清空归位与计数、重建托盘与版面、重新计时。
   *  口径与省份拼图一致：换一局就彻底换，不做「继续上局」。 */
  function newGame(msg) {
    stopTimer();
    feats = (MAP.features || {})[state.topic] || [];
    state.placed = {};
    state.placedCount = 0;
    state.miss = 0;
    state.hints = 0;
    state.elapsed = 0;
    state.done = false;
    state.selected = null;
    markTopicBtns();
    buildTray();
    buildSvg();
    updateHud();
    renderRecord();
    startTimer();
    say(msg || ('从托盘挑一个名字，点地图上它对应的线条或点——放对了给你讲它，放错了告诉你那里是谁。'));
  }

  function onTopic(btn) {
    var id = btn.dataset.stTopic;
    if (!id || id === state.topic || !((MAP.features || {})[id] || []).length) return;
    state.topic = id;
    newGame('已切换到「' + topicMeta().label + '」——题材换了就是新的一局，之前那局的成绩不带走。');
  }

  function startTimer() {
    stopTimer();
    state.timer = setInterval(function () {
      state.elapsed += 1000;
      el.time.textContent = '用时 ' + fmtTime(state.elapsed);
    }, 1000);
  }

  function onHint() {
    if (state.done) return;
    var f = state.selected;
    if (!f) {
      var left = feats.filter(function (x) { return !state.placed[x.id]; });
      if (!left.length) return;
      f = left[Math.floor(Math.random() * left.length)];
      selectFeature(f);
    }
    state.hints++;
    var n = nodes[f.id];
    n.classList.add('is-hint');
    setTimeout(function () { n.classList.remove('is-hint'); }, HINT_MS);
    say('「' + f.name + '」的示意走向在图上闪了——它在' + describeZone(f) + '。提示 ' + state.hints + ' 次，结算会如实计入。');
  }

  function onResetRecord() {
    // 连点两次确认（口径同省份拼图 / 连连看）：只清当前题材的成绩，题材互不牵连
    if (el.resetBtn.dataset.armed === '1') {
      try {
        var all = loadRecord();
        delete all[state.topic];
        localStorage.setItem(RECORD_KEY, JSON.stringify(all));
      } catch (e) { /* 忽略 */ }
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置这个题材的成绩';
      renderRecord();
      say('「' + topicMeta().label + '」的成绩已清空。');
      return;
    }
    el.resetBtn.dataset.armed = '1';
    el.resetBtn.textContent = '再点一次确认清空';
    setTimeout(function () {
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置这个题材的成绩';
    }, 3000);
  }

  /* ---- 启动 ---- */

  function boot() {
    if (!MAP || !MAP.features) return; // 数据文件未就位（加载顺序被破坏）时静默退出，不白屏
    el.map = document.querySelector('[data-st-map]');
    if (!el.map) return; // 只在山河归位页生效

    el.topics = document.querySelector('[data-st-topics]');
    el.tray = document.querySelector('[data-st-tray]');
    el.status = document.querySelector('[data-st-status]');
    el.progress = document.querySelector('[data-st-progress]');
    el.time = document.querySelector('[data-st-time]');
    el.record = document.querySelector('[data-st-record]');
    el.newBtn = document.querySelector('[data-st-new]');
    el.hintBtn = document.querySelector('[data-st-hint]');
    el.resetBtn = document.querySelector('[data-st-reset]');
    if (!el.tray || !el.status) return;

    if (el.topics) {
      // 事件委托：绑在容器上，currentTarget 是容器——题材数据在按钮的 dataset 上，
      // 必须从实际点击处（ev.target）向上找到按钮再交给 onTopic，否则恒为 undefined。
      el.topics.addEventListener('click', function (ev) {
        var t = ev.target;
        while (t && t !== el.topics && !(t.dataset && t.dataset.stTopic)) t = t.parentNode;
        if (t && t !== el.topics && t.dataset && t.dataset.stTopic) onTopic(t);
      });
    }
    el.newBtn.addEventListener('click', function () { newGame(); });
    el.hintBtn.addEventListener('click', onHint);
    if (el.resetBtn) el.resetBtn.addEventListener('click', onResetRecord);

    newGame();
  }

  boot();

  /* ---- 冒烟钩子（纯函数，供确定性用例） ---- */
  global.STShanmai = {
    distToPolyline: distToPolyline,
    nearestFeature: nearestFeature,
    bearingWord: bearingWord,
    missExplain: missExplain,
    shuffledIds: shuffledIds,
    describeZone: describeZone,
  };
})(typeof window !== 'undefined' ? window : this);
